import { useState, useEffect, useCallback } from 'react'
import { getPage } from '../lib/api'
import type { JobMatch } from '../types/api'

const BASE = import.meta.env.VITE_API_BASE ?? ''

function authHeaders(body?: unknown): Record<string, string> {
  const headers: Record<string, string> = {}
  if (import.meta.env.DEV) {
    headers['X-Dev-Bypass'] = '1'
  }
  if (body !== undefined) {
    headers['Content-Type'] = 'application/json'
  }
  return headers
}

export interface JobMatchFilters {
  tier: number | null
  minMatch: number | null
}

export interface RefreshOpts {
  query?: string
  years?: number
  limit?: number
}

export interface RefreshResult {
  created: number
  jobs: Array<{ id: string; company: string; position: string; matchPct: number }>
}

export function useJobMatches(
  talentId: string | undefined,
  filters: JobMatchFilters,
) {
  const [jobMatches, setJobMatches] = useState<JobMatch[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [refreshing, setRefreshing] = useState(false)

  const load = useCallback(async () => {
    if (!talentId) {
      setJobMatches([])
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams()
      if (filters.tier != null) params.set('tier', String(filters.tier))
      if (filters.minMatch != null) params.set('min_match', String(filters.minMatch))
      const qs = params.toString()
      const path = `/api/v1/talents/${talentId}/job-matches${qs ? `?${qs}` : ''}`
      const res = await getPage<JobMatch>(path)
      setJobMatches(res.data)
    } catch (e) {
      setError(e instanceof Error ? e.message : '알 수 없는 오류')
    } finally {
      setLoading(false)
    }
  }, [talentId, filters.tier, filters.minMatch])

  useEffect(() => {
    load()
  }, [load])

  const refresh = useCallback(
    async (opts?: RefreshOpts): Promise<RefreshResult> => {
      if (!talentId) throw new Error('인재가 선택되지 않았습니다')
      setRefreshing(true)
      const controller = new AbortController()
      const timer = setTimeout(() => controller.abort(), 30_000)
      try {
        const res = await fetch(
          `${BASE}/api/v1/talents/${talentId}/job-matches/refresh`,
          {
            method: 'POST',
            headers: authHeaders(opts),
            body: opts ? JSON.stringify(opts) : undefined,
            signal: controller.signal,
          },
        )
        const json = await res.json()
        if (json.error) throw new Error(json.error.message)
        await load()
        return json.data as RefreshResult
      } catch (e) {
        if (e instanceof DOMException && e.name === 'AbortError') {
          throw new Error('크롤링 시간 초과 (30s)')
        }
        throw e instanceof Error ? e : new Error('크롤링 실패')
      } finally {
        clearTimeout(timer)
        setRefreshing(false)
      }
    },
    [talentId, load],
  )

  return {
    jobMatches,
    loading,
    error,
    reload: load,
    refresh,
    refreshing,
  }
}
